import { Injectable, OnDestroy } from '@angular/core';
import { User, UserEventHandler } from '@sendbird/chat';
import {
  GroupChannel,
  GroupChannelHandler,
  GroupChannelHideParams,
  GroupChannelListOrder,
  GroupChannelListQuery,
  GroupChannelListQueryParams,
  GroupChannelUpdateParams,
  HiddenChannelFilter,
  QueryType,
  SendbirdGroupChat,
} from '@sendbird/chat/groupChannel';
import { PreviousMessageListQueryParams } from '@sendbird/chat/lib/__definition';
import { BaseMessage, PreviousMessageListQuery, UserMessage, UserMessageCreateParams } from '@sendbird/chat/message';
import { gql } from 'apollo-angular';
import { BehaviorSubject, Observable, Subject, Subscription, from } from 'rxjs';
import { concatMap, distinctUntilChanged, map, startWith } from 'rxjs/operators';
import { AuthService } from 'src/app/shared/services/auth.service';
import { ShareJobAndSendMessageResponse } from 'src/app/shared/types/chat.types';
import { Channel } from '../models/gql/channel.model';
import { Conversations } from '../models/gql/conversations.model';
import { SendBirdMessageData } from '../models/message.model';
import { User as ClientUser } from '../models/user.model';
import { AccountService } from './account.service';
import { ActionCableService } from './action-cable.service';
import { ApiInterfaceService } from './api-interface.service';
import { Broadcaster } from './ngx-cable.service';
import { CustomChannelType, SendBirdUser, directPermittedAccounts } from './sendbird/sb-connection-constants';
import { SbConnectionGqlService } from './sendbird/sb-connection-gql.service';
import { SbConnectionHandlersService } from './sendbird/sb-connection-handlers.service';
import { SbInstanceService } from './sendbird/sb-instance.service';

// Action cable channel name for sendBird conversation existance
const actionCableConstants = {
  convoExistanceAcChannel: 'ConversationExistChannel',
};

// meant to keep all sendBird related constant strings.
export const sendBirdConstants = {
  userEventHandlerId: 'ChatServiceUserEventHandler',
  channelListChannelHandlerId: 'ChannelListEventHandler',
  chatStreamChannelHandlerId: 'ChatStreamEventHandler',
  channelHandlerId: 'ChatServiceChannelHandler',
};

export const MessageStatus = {
  PENDING: 'pending',
  FAILED: 'failed',
  SENT: 'sent',
  DELIVERED: 'delivered',
  READ: 'read',
};

export const ChannelType = {
  OneOnOne: CustomChannelType.OneOnOne,
  GroupChat: CustomChannelType.GroupChat,
};

@Injectable({
  providedIn: 'root',
})
export class ChatService implements OnDestroy {
  // Sendbird instance
  sendBird: SendbirdGroupChat;

  // Listen to sendbird connection
  private sendbirdConnectedUser = new BehaviorSubject<User>(null);

  // Conversation exists behavior subject  - This will be used to make decisions that depends on action cable response.
  private conversionExist = new BehaviorSubject<boolean>(null);

  // Sendbird connection failed behavior subject - This is mainly used to reconnect in the Inbox page when set to TRUE.
  private sendbirdConnectionFailed = new BehaviorSubject<boolean>(false);

  // Conversation exists channel subscription
  private conversationExistChannelSub: Subscription;

  // Conversation broadcaster subscription
  private conversationExistSubscription: Subscription;

  // Stores & emits the sendbird total user unreadCount.
  private unreadCount$ = new BehaviorSubject<number>(0);

  // Stores & emits the sendbird user typing channel and a re-render trigger to avoid impure pipe.
  private channelTypingIndicator$ = new BehaviorSubject<{
    channel: GroupChannel;
    rerenderTrigger: string;
  }>(null);

  // Account status channel subscription
  private accountStatusChannelSub;

  // Account status broadcaster subscription
  private accountStatusSubscription: Subscription;

  // Returns true of the primary logged in user is admin or organisation.
  isLoggedInUserAdminOrOrg = false;

  private destroy$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private actionCableService: ActionCableService,
    private broadcaster: Broadcaster,
    private api: ApiInterfaceService,
    private accountService: AccountService,
    private sbInstanceService: SbInstanceService,
    private sbConnectionHandlersService: SbConnectionHandlersService,
    private sbConnectionGqlService: SbConnectionGqlService,
  ) {
    // Created sendbird instance
    this.sendBird = this.sbInstanceService.instance;

    // Disconnect user on logout if it's connected
    this.authService.userAuthStatusSubject.pipe(distinctUntilChanged()).subscribe(async (isLoggedIn) => {
      if (isLoggedIn) {
        this.isLoggedInUserAdminOrOrg =
          this.accountService.userIs('admin', true) || this.accountService.userIs('organisation', true);

        this.subscribeToAccountStatus();
        const user = this.accountService.getUser();
        if (directPermittedAccounts.includes(user?.custom?.account_type)) {
          await this.connectUser();
        }
      } else {
        await this.disconnect();
        this.unsubscribeFromAccountStatusChannel();
      }
    });
  }

  ngOnDestroy() {
    this.unsubscribeFromConversationExists();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Checks whether the user account is valid (approved) in case of client & worker.
   * Checks whether the client is valid (approved) in case of coordinator.
   *
   * @param clientDetail - Client detail in case of coordinator.
   */
  async isUserApproved(clientDetail?: ClientUser): Promise<boolean> {
    const currentUser = this.accountService.getUser();
    if (!currentUser) {
      console.warn('[Inbox] user is EMPTY on user approved check');
    }

    // if carer or consumer, return true is approved
    if (directPermittedAccounts.includes(currentUser?.custom?.account_type)) {
      return this.accountService?.user?.approved === true;
    }

    // check whether the client is approved.
    return clientDetail?.approved === true;
  }

  /**
   * Connects user on auth change or coordinator session switch.
   *
   * @param clientDetail - This is used to connect to sendBird on behalf of another user. For example, quick actions in coordinator workflow.
   */
  async connectUser(clientDetail?: ClientUser) {
    // Disconnect old user to cleanup existing channel subscriptions and user data.
    this.sendbirdConnectionFailed.next(false);
    await this.disconnect();
    this.unsubscribeFromConversationExists();
    this.subscribeToConversationExists(clientDetail);
  }

  /**
   * Disconnect user from sendbird.
   * Use cases: logout, channels existance returned false, user unapproved
   */
  async disconnect() {
    try {
      this.unreadCount$.next(null); // resetting the unread count.
      await this.sbInstanceService.disconnectUser();
      this.sendbirdConnectedUser.next(null);
      this.removeChannelEventHandler(sendBirdConstants.channelHandlerId);
      this.removeUserEventHandler(sendBirdConstants.userEventHandlerId);
      return null;
    } catch (error) {
      console.error('failed to disconnect sendbird', error);
      return Promise.reject(error.message);
    }
  }

  /**
   * Check if conversation exists in the backend.
   *
   * @param clientId - This is used to connect to sendBird on behalf of another user. For example, quick actions in coordinator workflow.
   */
  private async getConversationExistance(clientId?: number | string): Promise<boolean> {
    try {
      // Append client_id params for coordinators and orgs
      let queryParam = '';
      if (clientId) {
        queryParam = `?client_id=${clientId}`;
      }

      const res = await (<any>(await this.api.get(`/conversations/exist${queryParam}`, true)).toPromise());

      // it may not exist or defined - coerce to FALSE
      return !!res?.conversation_exists;
    } catch (error) {
      console.error('[Inbox] Failed to fetch conversation exist', error);
      return false;
    }
  }

  /**
   * Returns sendbird user observable only -
   * Its to prevent anyone from updating the sendbirdConnectedUser value.
   */
  getSendBirdUser(): Observable<SendBirdUser> {
    return this.sendbirdConnectedUser.asObservable();
  }

  /**
   * Returns unread count observable only -
   * Its to prevent anyone from updating the unread count value.
   */
  getUnreadCount(): Observable<number> {
    return this.unreadCount$.asObservable();
  }

  /**
   * Returns channel typing indicator observable only -
   * Its to prevent anyone from updating the channel typing indicator.
   */
  getChannelTypingIndicator(): Observable<{
    channel: GroupChannel;
    rerenderTrigger: string;
  }> {
    return this.channelTypingIndicator$.asObservable();
  }

  /**
   * Connect user to sendbird.
   * It should never be called directly as the connection should only be established after checking the conversation existence status from action cable.
   *
   *  @param clientDetail - This is used to connect to sendBird on behalf of another user. For example, quick actions in coordinator workflow.
   */
  private async connectUserToSendBird(clientDetail?: ClientUser) {
    // Connect only if account is approved.
    const isApproved = await this.isUserApproved(clientDetail);
    if (!isApproved) {
      return null;
    }

    try {
      // Return logged in user if already connected to sendbird
      const sendBirdLoggedInUser = this.sbInstanceService.currentUser;
      if (sendBirdLoggedInUser) return;

      // Set session handler to refresh the token automatically
      this.sbConnectionHandlersService.setSessionHandler();

      if (clientDetail) {
        // if the client detail is provided, fetch token for given client and connect senbird for that client.
        const sendbirdAccessToken = await this.sbConnectionGqlService.generateSendbirdSessionToken(
          `${clientDetail.uid}`,
        );
        await this.sendBird.connect(clientDetail?.uid, sendbirdAccessToken);
      } else {
        // otherwise, connect sendbird for the logged in user.
        const currentUser = this.accountService.getUser();
        const sendbirdAccessToken = await this.sbConnectionGqlService.generateSendbirdSessionToken();
        await this.sendBird.connect(currentUser?.custom?.user_uid, sendbirdAccessToken);
      }

      // start watching unread count as soon as connected.
      this.watchForUnreadCount();

      // start watching channel events typing status.
      this.watchForChannelEvents();

      this.sendbirdConnectedUser.next(this.sbInstanceService.currentUser);
      // Update the foreground state right after login as it seems the state is not updated in offline mode
      // Required for push notifications device registration
      this.sendBird.setForegroundState();
    } catch (error) {
      console.error('[Inbox] Failed to connect user to sendbird', error);
      this.sendbirdConnectionFailed.next(true);
      return Promise.reject(error);
    }
  }

  /**
   * Subscribe to the conversation exist channel.
   * ConversationExistChannel is a channel which gives updated flag for conversation count.
   *
   * @param clientId - The clientId if we need to connect for specific client.
   */
  async subscribeToConvoExistenceAcChannel(clientId?: number) {
    this.conversationExistChannelSub = await this.actionCableService.subscribeToChannel(
      actionCableConstants.convoExistanceAcChannel,
      { client_id: clientId },
    );
  }

  /**
   * Subscribe to conversation exists channel to check if conversation exists for user. Listen to channel broadcaster to get updated details.
   *
   * @param clientDetail - Client detail object of the client, used when connecting on behalf of another user. For ex: coordinator quick-reply flow.
   */
  private async subscribeToConversationExists(clientDetail?: ClientUser): Promise<void> {
    await this.subscribeToConvoExistenceAcChannel(clientDetail?.id);

    // Initiate observable with direct backend call response. This is done to avoid the wait time between action cable subscription and first value broadcast.
    const onDemandConversationExists = await this.getConversationExistance(clientDetail?.id);

    this.conversationExistSubscription = this.broadcaster
      .on(actionCableConstants.convoExistanceAcChannel)
      .pipe(
        startWith({ conversation_exists: onDemandConversationExists }),
        map((res: any) => res?.conversation_exists), // Fetching value since distinctUntilChanged not working as expected on object
        distinctUntilChanged(), // Only fire when the value is changed.
      )
      .subscribe(
        async (conversationExists) => {
          if (conversationExists) {
            await this.connectUserToSendBird(clientDetail);
          } else {
            // TODO: Is it ever gonna happen? Do we need disconnect? Will disconnect count as MAU?
            await this.disconnect();
          }
          this.conversionExist.next(conversationExists);
        },
        (error) => {
          console.error('[Inbox] Error in subscription to conversation exist', error);
          this.sendbirdConnectionFailed.next(true);
        },
      );
  }

  /**
   * Unsubscribe to conversation exists channel.
   *
   */
  unsubscribeFromConversationExists(): void {
    this.conversationExistSubscription?.unsubscribe();
    if (this.conversationExistChannelSub) {
      this.actionCableService.removeChannel(this.conversationExistChannelSub);
      this.conversationExistChannelSub = null;
    }
  }

  /**
   * Returns conversation exists observable
   */
  getConversationExists(): Observable<boolean> {
    return this.conversionExist.asObservable();
  }

  /**
   * Returns sendbird connection failed value observable
   */
  getSendbirdConnectionFailed(): Observable<boolean> {
    return this.sendbirdConnectionFailed.asObservable();
  }

  /**
   * Unsubscribe to account status channel.
   *
   */
  unsubscribeFromAccountStatusChannel(): void {
    this.accountStatusSubscription?.unsubscribe();
    if (this.accountStatusChannelSub) {
      this.actionCableService.removeChannel(this.accountStatusChannelSub);
      this.accountStatusChannelSub = null;
    }
  }

  /**
   * Subscribe to the account status channel.
   * Account status channel is a channel which gives updates on account status change.
   *
   */
  async subscribeToAccountChannel() {
    this.accountStatusChannelSub = await this.actionCableService.subscribeToChannel('UserApprovalStatusChannel');
  }

  /**
   * Subscribe to account status channel to get user approval status. Listen to account status channel broadcaster to get updated user approval status.
   */
  private async subscribeToAccountStatus(): Promise<void> {
    try {
      await this.subscribeToAccountChannel();
      this.accountStatusSubscription = this.broadcaster
        .on('UserApprovalStatusChannel')
        .pipe(
          map((res: any) => res?.approved), // Fetching value since distinctUntilChanged not working as expected on object
          distinctUntilChanged(), // Only fire when the value is changed.
        )
        .subscribe(
          (isApproved) => {
            if (this.accountService.user?.approved !== isApproved) {
              this.accountService.user.approved = isApproved;
              if (isApproved) {
                this.connectUserToSendBird();
              } else {
                this.disconnect();
              }
            }
          },
          (error) => {
            console.error('[Inbox] Failed to check user approval via AC', error);
          },
        );
    } catch (error) {
      console.error('[Inbox] Failed to subscribe to account channel', error);
    }
  }

  /**
   * To receive information about events related to users connected to Sendbird server, you need to add a user event handler with its unique user-defined ID.
   * https://sendbird.com/docs/chat/v3/javascript/guides/event-handler#2-add-and-remove-a-user-event-handler
   *
   * @param uniqueID - Unique handler id
   * @param userEventHandler - User event handler object
   */
  addUserEventHandler(uniqueID: string, userEventHandler) {
    this.removeUserEventHandler(uniqueID);
    this.sendBird.addUserEventHandler(uniqueID, userEventHandler);
  }

  /**
   * Remove user event handler
   *
   * @param uniqueID - Unique handler id
   */
  removeUserEventHandler(uniqueID: string) {
    this.sendBird.removeUserEventHandler(uniqueID);
  }

  /**
   * To receive and retrieve information about certain events happening in the channels from Sendbird server, you need to register a channel handler with its unique user-defined ID.
   * https://sendbird.com/docs/chat/v3/dotnet/guides/event-handler#2-add-and-remove-a-channel-handler
   * Not specifying type to use onDeliveryReceiptUpdated event. Because this event is not added to SendBird.d.ts.
   *
   * @param uniqueID - Unique handler id
   * @param channelHandler - Channel handler object
   */
  addChannelEventHandler(uniqueID: string, channelHandler) {
    this.removeChannelEventHandler(uniqueID);
    this.sendBird.groupChannel.addGroupChannelHandler(uniqueID, channelHandler);
  }

  /**
   * Remove channel event handler
   *
   * @param uniqueID - Unique handler id
   */
  removeChannelEventHandler(uniqueID: string) {
    this.sendBird.groupChannel.removeGroupChannelHandler(uniqueID);
  }

  /**
   * Watch channel events lik Read/watch channel typing status
   */
  watchForChannelEvents() {
    const channelHandler: GroupChannelHandler = new GroupChannelHandler({
      onTypingStatusUpdated: (channel: GroupChannel) => {
        this.channelTypingIndicator$.next({
          channel,
          rerenderTrigger: new Date().getTime().toString(),
        });
      },
    });

    this.addChannelEventHandler(sendBirdConstants.channelHandlerId, channelHandler);
  }

  /**
   * Read/watch for unread count.
   */
  async watchForUnreadCount() {
    try {
      // read the unread count on fly/load.
      const count = await this.sendBird.groupChannel.getTotalUnreadMessageCount();
      this.unreadCount$.next(count);

      // register a event handler.
      const userEventHandler: UserEventHandler = new UserEventHandler();

      // this will run when there is a change in unread count.
      userEventHandler.onTotalUnreadMessageCountUpdated = (totalCount) => {
        this.unreadCount$.next(+totalCount);
      };

      this.addUserEventHandler(sendBirdConstants.userEventHandlerId, userEventHandler);
    } catch (error) {
      console.error('[Inbox] Failed to get total unread message count', error);
    }
  }

  /**
   * Executes the callback if the user is not an admin or org.
   * E.g. Sets channel message delivery and read statuses if not admin/org.
   *
   * @param callback - Action to perform if the user is not an admin or org.
   */
  updateChannelStatusIfAllowed(callback = () => {}): void {
    if (!(this.accountService.userIs('admin', true) || this.accountService.userIs('organisation', true))) {
      callback();
    }
  }

  /**
   * Returns channel list query.
   *
   * @param additionalParams
   */
  getChannelQuery(additionalParams: GroupChannelListQueryParams = {}): GroupChannelListQuery {
    const params: GroupChannelListQueryParams = {
      includeEmpty: true,
      order: GroupChannelListOrder.LATEST_LAST_MESSAGE,
      limit: 25, // The value of the pagination limit could be set up to 100.
      ...additionalParams,
    };

    return this.sendBird.groupChannel.createMyGroupChannelListQuery(params);
  }

  /**
   * Get existing channels based on channel query
   *
   * @param channelListQuery - Channel list query
   */
  async getChannels(channelListQuery: GroupChannelListQuery): Promise<Array<GroupChannel>> {
    try {
      const channelList: GroupChannel[] = await channelListQuery.next();
      await this.markAsDelivered(channelList);
      return channelList;
    } catch (error) {
      console.error('[Inbox] Failed to fetch channel list', error);
      return Promise.reject(error.message);
    }
  }

  /**
   * Returns channel list query.
   */
  getArchivedChannelQuery(): GroupChannelListQuery {
    return this.getChannelQuery({ hiddenChannelFilter: HiddenChannelFilter.HIDDEN });
  }

  /**
   * Get channel using url
   *
   * @param channelUrl - Channel url
   */
  async getChannel(channelUrl: string): Promise<GroupChannel> {
    try {
      return await this.sendBird.groupChannel.getChannel(channelUrl);
    } catch (error) {
      console.error('[Inbox] Failed to fetch channel', error);
      return Promise.reject(error.message);
    }
  }

  /**
   * Archive/Hide channel
   *
   * @param channel - Channel instance
   */
  async archiveChannel(channel: GroupChannel): Promise<Record<string, any>> {
    try {
      const params: GroupChannelHideParams = {
        hidePreviousMessages: false,
        allowAutoUnhide: false,
      };

      return channel.hide(params);
    } catch (error) {
      return Promise.reject(error.message);
    }
  }

  /**
   * Unarchive/Unhide channel
   *
   * @param channel - Channel instance
   */
  async unarchiveChannel(channel: GroupChannel): Promise<Record<string, any>> {
    try {
      return await channel.unhide();
    } catch (error) {
      return Promise.reject(error.message);
    }
  }

  /**
   * Create 1-1 channel if not exist
   *
   * @param id - Participant user id
   */
  async createOneToOneChannel(id): Promise<Channel> {
    try {
      const createSendbirdChannelInput = {
        attributes: {
          type: CustomChannelType.OneOnOne,
          participantIds: [Number(id)],
        },
      };
      const resChannel = await this.api.gqlMutation(
        gql`
          mutation ($createSendbirdChannelInput: CreateSendbirdChannelInput!) {
            createSendbirdChannel(input: $createSendbirdChannelInput) {
              createSendbirdChannelResponse {
                isConversationEmpty
                sendbirdChannelDetail {
                  channelType
                  channelUrl
                }
              }
            }
          }
        `,
        { createSendbirdChannelInput },
      );

      return resChannel['createSendbirdChannel']?.createSendbirdChannelResponse;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Returns message list query.
   *
   * @param channel
   */
  getMessagesQuery(channel: GroupChannel): PreviousMessageListQuery {
    const previousMessageListQuery: PreviousMessageListQueryParams = channel.createPreviousMessageListQuery();
    previousMessageListQuery.limit = 20;
    return channel.createPreviousMessageListQuery(previousMessageListQuery);
  }

  /**
   * Get messages
   *
   * @param messageListQuery
   */
  async getMessages(messageListQuery: PreviousMessageListQuery): Promise<Array<BaseMessage>> {
    try {
      return await messageListQuery.load();
    } catch (error) {
      return Promise.reject(error.message);
    }
  }

  /**
   * Marks the given channel or list of channels messages as delivered.
   *
   * @param channels - channel or list of channels to be marked as delivered.
   */
  async markAsDelivered(channels: GroupChannel | GroupChannel[]) {
    const markAsDelivered = (channel) => {
      // We only need to mark channel as delivered if there are any new messages.
      if (channel.unreadMessageCount) {
        this.sendBird.groupChannel
          .markAsDelivered(channel.url)
          .catch((error) => console.error('Error in markAsDelivered', error, JSON.stringify(error)));
      }
    };

    if (!this.isLoggedInUserAdminOrOrg) {
      try {
        if (Array.isArray(channels)) {
          // Any short-lived observable will complete on its own.
          // This is the case with promises, HTTP calls, observables created with of or from(array).
          // These generally don't need to be unsubscribed unless some business logic dictates otherwise.
          from(channels)
            .pipe(concatMap(async (channel: GroupChannel) => markAsDelivered(channel)))
            .subscribe();
        } else {
          markAsDelivered(channels);
        }
      } catch (error) {
        Promise.reject(error);
      }
    }
  }

  /**
   * Send text message
   *
   * @param channel - Group channel object
   * @param messageText - Message string
   * @param metaData - message meta data
   */
  sendTextMessage(channel: GroupChannel, messageText: string, metaData: SendBirdMessageData): Promise<UserMessage> {
    const data = { ...metaData, kind: 'message ', session_type: 'mobile' };
    // Send user message method doesn't return Promise and also has callback required. So using return promise.
    return new Promise((resolve, reject) => {
      const params: UserMessageCreateParams = {
        customType: 'message',
        message: messageText,
        data: JSON.stringify(data), // stringified json to be sent, else blank.
      };
      channel
        .sendUserMessage(params)
        .onSucceeded((message: UserMessage) => {
          resolve(message);
        })
        .onFailed((error) => {
          reject(error);
        });
    });
  }

  /**
   * Fetch conversation details for one on one chat using channel url
   *
   * @param channelUrl - Channel url
   */
  async getConversationByChannelUrl(channelUrl: string): Promise<Conversations> {
    try {
      const res = await this.api.gqlQuery(
        gql`
          query sendbirdChannelDetail($channelUrl: String!) {
            sendbirdChannelDetail(channelUrl: $channelUrl) {
              id
              conversation {
                id
                isHcpManagedOrOtherManagedClient
                sendbirdChannelDetail {
                  channelUrl
                }
                otherUser {
                  id
                  uuid
                  displayName
                  firstName
                  avatar {
                    small
                  }
                  permalink
                  normalizedType
                }
                currentUser {
                  id
                  uid
                  uuid
                  displayName
                }
                allowedActions {
                  meetAndGreet {
                    isAllowed
                    bannerDismissed24HoursAgo
                  }
                  videoCall {
                    isAllowed
                  }
                  phoneCall {
                    isAllowed
                  }
                }
                meetAndGreet {
                  id
                  status
                  scheduledAt
                  requestor {
                    id
                  }
                }
                status {
                  archived
                  userHasBlocked
                  userIsBlocked
                  userHasBlockedCalls
                  showCircumventionBanner
                  showShareYourJobBanner
                  hasReviewableAgreement
                }
                clientFields {
                  hasReviewableAgreement
                }
                hasAgreement
                agreement {
                  id
                  isRequested
                  isAccepted
                  status
                }
                offeredFlexMatch {
                  user {
                    id
                    displayName
                    firstName
                  }
                  postedJob {
                    id
                  }
                }
              }
            }
          }
        `,
        { channelUrl },
      );

      const conversation: Conversations = res['sendbirdChannelDetail']?.conversation;
      return conversation;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Fetch conversation details for group chat using channel url
   *
   * @param channelUrl - Channel url
   */
  async getConversationForGroupByChannelUrl(channelUrl: string): Promise<Conversations> {
    try {
      const res = await this.api.gqlQuery(
        gql`
          query sendbirdChannelDetail($channelUrl: String!) {
            sendbirdChannelDetail(channelUrl: $channelUrl) {
              id
              conversation {
                id
                currentUser {
                  id
                  uid
                  uuid
                  displayName
                  firstName
                  fullName
                  avatar {
                    small
                  }
                  profile {
                    ... on ClientProfile {
                      typeOfCare
                    }
                  }
                }
                otherUsers {
                  id
                  uid
                  uuid
                  displayName
                  firstName
                  fullName
                  avatar {
                    small
                  }
                  profile {
                    ... on ClientProfile {
                      typeOfCare
                    }
                  }
                }
              }
            }
          }
        `,
        { channelUrl },
      );

      // parsing displayNames of otherUsers in a map
      const conversation = res['sendbirdChannelDetail']?.conversation;
      const displayNames = {};
      conversation?.otherUsers?.forEach((user) => {
        displayNames[user.uid] = user.displayName;
      });
      conversation.displayNames = displayNames;
      return conversation;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Fetch conversation actions for one on one chat using channel url
   *
   * @param channelUrl - Channel url
   */
  async getConversationActionsByChannelUrl(channelUrl: string): Promise<any> {
    try {
      const res = await this.api.gqlQuery(
        gql`
          query sendbirdChannelDetail($channelUrl: String!) {
            sendbirdChannelDetail(channelUrl: $channelUrl) {
              id
              conversation {
                id
                allowedActions {
                  meetAndGreet {
                    isAllowed
                  }
                  videoCall {
                    isAllowed
                  }
                  phoneCall {
                    isAllowed
                  }
                }
              }
            }
          }
        `,
        { channelUrl },
      );

      const allowedActions = res['sendbirdChannelDetail']?.conversation?.allowedActions;
      return allowedActions;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Send job message on share job
   *
   * @param sharePostedJobInput
   * @param sharePostedJobInput.postedJobId
   * @param sharePostedJobInput.sendbirdChannelUrl
   * @param sharePostedJobInput.body
   * @param sharePostedJobInput.mechanism
   */
  async postJobMessage(sharePostedJobInput: {
    postedJobId: number;
    sendbirdChannelUrl: string;
    body: string;
    mechanism?: string;
  }) {
    try {
      const resChannel = await this.api.gqlMutation(
        gql`
          mutation ($sharePostedJobInput: SharePostedJobInput!) {
            sharePostedJob(input: $sharePostedJobInput) {
              sharePostedJobResponse {
                status
              }
            }
          }
        `,
        { sharePostedJobInput },
      );

      return resChannel['sharePostedJob']?.sharePostedJobResponse;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Shares a job with a worker. Will send the worker a message in their inbox and mark the job as shared with said
   * worker. If the job's been previously marked as `filled` and has a terminated agreement associated with it, it will
   * create a new private job and return its details. If this has happened before, it will return that existing
   * duplicate private job. If the job's `open` it will simply share the open job.
   *
   * @param postedJobId - The job the client wishes to share.
   * @param sendbirdChannelUrl - The URL of the sendbird channel, required to share the job with the worker.
   * @param chatMessage - The message to share with the worker from the client.
   */
  shareJobAndSendMessage(
    postedJobId: number,
    sendbirdChannelUrl: string,
    chatMessage: string,
  ): Observable<ShareJobAndSendMessageResponse> {
    const url = `/api/v1/clients/posted_jobs/${postedJobId}/share`;
    const body = {
      chat_message: chatMessage,
    };

    const optionalParams = {
      sendbird_channel_url: sendbirdChannelUrl,
      job_id: postedJobId,
    };

    return this.api.post$(url, body, true, optionalParams);
  }

  /**
   * Create group conversation if not exist
   *
   * @param participantIds - Participant user ids array
   * @param channelName - Channel name
   */
  async createGroupConversation(participantIds: number[], channelName?: string): Promise<any> {
    try {
      const createSendbirdChannelInput = {
        attributes: {
          type: CustomChannelType.GroupChat,
          participantIds,
          channelName: channelName || null, // Pass null if channel name is empty
        },
      };
      const resChannel = await this.api.gqlMutation(
        gql`
          mutation ($createSendbirdChannelInput: CreateSendbirdChannelInput!) {
            createSendbirdChannel(input: $createSendbirdChannelInput) {
              createSendbirdChannelResponse {
                isConversationEmpty
                sendbirdChannelDetail {
                  channelType
                  channelUrl
                }
              }
            }
          }
        `,
        { createSendbirdChannelInput },
      );

      return resChannel['createSendbirdChannel']?.createSendbirdChannelResponse;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Send sigle link message
   *
   * @param channel - Group channel object
   * @param messageText - Message string
   * @param link_text - Link string
   * @param link_url - Link url
   */
  sendSingleLinkMessage(
    channel: GroupChannel,
    messageText: string,
    link_text: string,
    link_url: string,
  ): Promise<UserMessage> {
    // Send user message method doesn't return Promise and also has callback required. So using return promise.
    return new Promise((resolve, reject) => {
      const params: UserMessageCreateParams = {
        customType: 'single_link',
        message: messageText,
        data: JSON.stringify({
          kind: 'single_link',
          session_type: 'mobile',
          meta: {
            link_text,
            link_url,
          },
        }),
      };

      channel
        .sendUserMessage(params)
        .onSucceeded((message: UserMessage) => {
          resolve(message);
        })
        .onFailed((error) => {
          reject(error);
        });
    });
  }

  /**
   * Sendbird call to leave a group.
   *
   * @param channel - Channel instance.
   */
  async leaveGroupChannel(channel: GroupChannel): Promise<void> {
    try {
      // freeze channel if there is only one operator & he/she leaves the group.
      const shouldFreeze =
        channel.myRole === 'operator' && channel.members?.filter((member) => member.role === 'operator')?.length === 1;

      if (shouldFreeze) {
        // freeze the channel if role is operator (client)
        return await channel.freeze();
      }
      // leave the channel
      return await channel.leave();
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Sendbird call to unfreeze channel
   *
   * @param channel - Channel instance
   */
  async unfreezeChannel(channel: GroupChannel): Promise<void> {
    try {
      if (channel.myRole === 'operator') {
        return await channel.unfreeze();
      }
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /*
   * Check if messages are exchanged based on message types
   *
   * @param channelUrl - Group channel url
   * @param customTypesFilter - set of custom types to filter messages
   */
  async isMessagesExchanged(channelUrl: string, customTypesFilter?: string[]): Promise<boolean> {
    const currentUser = this.sbInstanceService.currentUser;
    if (channelUrl && currentUser) {
      try {
        const channel = await this.getChannel(channelUrl);
        if (!channel.lastMessage) {
          return false;
        }
        // We have to make 2 separate call to fetch messages for current user and participant because sendbird doesn't have method to get messages count for mutliple users.
        const requestArray = [];
        // Fetch current user message. Giving type any to bypass compilation error on customTypesFilter due to incorrect data type added in sendbird.d.ts
        const currentUserQueryParams: PreviousMessageListQueryParams = {
          senderUserIdsFilter: [currentUser.userId],
          limit: 1,
        };
        if (customTypesFilter) {
          currentUserQueryParams.customTypesFilter = customTypesFilter;
        }
        const currentUserMessageQuery: PreviousMessageListQuery =
          channel.createPreviousMessageListQuery(currentUserQueryParams);
        requestArray.push(currentUserMessageQuery.load());

        // Fetch participant user message
        const participant = channel?.members.find((x) => x.userId !== currentUser?.userId);
        // Giving type any to bypass compilation error on customTypesFilter due to incorrect data type added in sendbird.d.ts

        const participantQueryParams: PreviousMessageListQueryParams = {
          senderUserIdsFilter: [participant?.userId],
          limit: 1,
        };
        if (customTypesFilter) {
          participantQueryParams.customTypesFilter = customTypesFilter;
        }
        const participantUserMessageQuery: PreviousMessageListQuery =
          channel.createPreviousMessageListQuery(participantQueryParams);

        requestArray.push(participantUserMessageQuery.load());

        const res = await Promise.all(requestArray);
        return res.length === 2 && res[0].length > 0 && res[1].length > 0;
      } catch (error) {
        return Promise.reject(error);
      }
    }

    return false;
  }

  /**
   * Removes a user from the channel.
   *
   * @param channel - Sendbird channel instance.
   * @param userIds
   */
  async addPeopleToGroup(channel: GroupChannel, userIds: string[]): Promise<GroupChannel> {
    try {
      return await channel.inviteWithUserIds(userIds);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Removes a user from the channel.
   *
   * @param channelUrl
   * @param channelName
   * @param userId
   */
  async removeUserFromGroup(channelUrl: string, channelName: string, userId: string): Promise<boolean> {
    try {
      const removeUserSendbirdChannelInput = {
        attributes: {
          channelUrl,
          channelName,
          removedUserUid: [userId],
        },
      };
      const resChannel = await this.api.gqlMutation(
        gql`
          mutation ($removeUserSendbirdChannelInput: RemoveUserSendbirdChannelInput!) {
            removeUserSendbirdChannel(input: $removeUserSendbirdChannelInput) {
              removeUserSendbirdChannelResponse {
                userRemovedSuccessfully
              }
            }
          }
        `,
        { removeUserSendbirdChannelInput },
      );

      return resChannel['removeUserSendbirdChannel']?.removeUserSendbirdChannelResponse?.userRemovedSuccessfully;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Update group details.
   *
   * @param channel - Channel instance
   * @param name - channel name
   */
  async updateGroupDetails(channel: GroupChannel, name: string): Promise<GroupChannel> {
    try {
      const params: GroupChannelUpdateParams = {
        name,
      };
      return await channel.updateChannel(params);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Sends request to BE to trigger system nudge to remind customers about privacy protection options.
   *
   * @param channelUrl - sendbird channel URL
   * @returns - string response
   */
  triggerPrivacyProtectionNudge(channelUrl: string): Observable<string> {
    // TODO (ES-19856): Replace Query Type with the one generated from GQL Schema
    type PrivacyProtectionNudgeMutationResponse = {
      triggerPrivacyProtectionNudge?: {
        response?: string;
      };
    };
    return this.api
      .gqlMutation$<PrivacyProtectionNudgeMutationResponse>(
        gql`
          mutation ($TriggerPrivacyProtectionNudgeInput: TriggerPrivacyProtectionNudgeInput!) {
            triggerPrivacyProtectionNudge(input: $TriggerPrivacyProtectionNudgeInput) {
              response
            }
          }
        `,
        { TriggerPrivacyProtectionNudgeInput: { channelUrl } },
      )
      .pipe(map((result) => result.triggerPrivacyProtectionNudge?.response));
  }

  /**
   * Finds a group channel based on the requester and responder IDs.
   *
   * @param requesterId - The ID of the requester.
   * @param responderId - The ID of the responder.
   * returns a promise that resolves to the found group channel, or undefined if not found.
   */
  async findChannel(requesterId: string, responderId: string): Promise<GroupChannel | undefined> {
    const params: GroupChannelListQueryParams = {
      includeEmpty: true,
      order: GroupChannelListOrder.LATEST_LAST_MESSAGE,
      limit: 1,
      userIdsFilter: {
        queryType: QueryType.AND,
        userIds: [requesterId, responderId],
        includeMode: true,
      },
      hiddenChannelFilter: HiddenChannelFilter.ALL,
      customTypesFilter: ['one_on_one'],
    };
    const query: GroupChannelListQuery = this.sendBird.groupChannel.createMyGroupChannelListQuery(params);
    const channelList: GroupChannel[] = await query.next();
    return channelList?.[0];
  }

  /**
   * Unarchive/Unhide conversation.
   *
   * @param channel - Channel instance.
   */
  async unarchiveConversation(channel: GroupChannel): Promise<GroupChannel> {
    try {
      return await channel.unhide();
    } catch (error) {
      return Promise.reject(error.message);
    }
  }

  /**
   * Fetches the conversation object based on channel url.
   *
   * @param channelUrl - Sendbird channel url.
   */
  async getConversationDetailsByChannelUrl(channelUrl: string): Promise<Conversations> {
    try {
      const res = await this.api.gqlQuery(
        gql`
          query sendbirdChannelDetail($channelUrl: String!) {
            sendbirdChannelDetail(channelUrl: $channelUrl) {
              id
              conversation {
                id
                isHcpManagedOrOtherManagedClient
                sendbirdChannelDetail {
                  channelUrl
                }
                meetAndGreet {
                  id
                  status
                  scheduledAt
                  requestor {
                    id
                  }
                }
                status {
                  archived
                  userHasBlocked
                  userIsBlocked
                  userHasBlockedCalls
                  showCircumventionBanner
                  showShareYourJobBanner
                  hasReviewableAgreement
                }
                clientFields {
                  hasReviewableAgreement
                }
                hasAgreement
              }
            }
          }
        `,
        {
          channelUrl,
        },
      );
      return res['sendbirdChannelDetail']?.conversation;
    } catch (e) {
      return Promise.reject(e);
    }
  }
}
